// pdfRender.ts — host-side helpers for assembling the PDF request body from
// the live scene state, capturing Unity screenshots against a PdfLayout's
// slot mapping, and firing the download or email endpoint.
//
// Lives here (not in CartModal) because the render-pdf-* actions need to fire
// from anywhere — toolbar, footer, custom block — not just from inside the
// cart. The cart, when it still owned the buttons, was just one caller of
// this code; now there can be many.

import {
  captureUnityScreenshot,
  collectUnityScreenshotRequests,
  downloadBlob,
  emailPdf,
  renderPdf,
  sendEmailLayout,
  type EmailLayoutSendBody,
  type PdfEmailFields,
  type PdfRenderRequestBody,
  type PdfSceneSummaryContext,
  type PdfSummaryRowDto,
} from "./pdfLayoutClient";
import { fetchProductIds } from "./catalogClient";
import type { BreakdownLineDto, PriceBreakdownDto } from "./priceClient";
import { resolveRowMaterials } from "../materialResolve";
import { formatDecimal } from "../measurement";
import type { SceneInstance, SceneProductDetailResponse, SceneProductDto } from "../types";

/** Conservative HTML escape for the operator's free-text email message before
 *  wrapping it in an HTML body. The server doesn't trust the body either, but
 *  this keeps a stray < from breaking the email's MIME structure on the way in. */
export function escapeMessageHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export interface PdfRenderContextInputs {
  baseUrl?: string;
  /** Every placed instance the cart would otherwise see. */
  instances: SceneInstance[];
  /** Catalog product map (name + image + parameterValues). */
  productById: Map<string, SceneProductDto>;
  /** Product-detail cache, used to resolve each row's material picks to display
   *  names for the pdf-product-materials block. Caller should warm it for the
   *  placed products first (resolution is best-effort — unresolved rows just
   *  ship no materials). */
  detailCache?: Map<string, SceneProductDetailResponse>;
  /** Resolved scene dimensions in metres (Unity's GetSceneDimensions reply).
   *  Null when the viewer isn't ready or the scene is empty. */
  dims: { width: number; height: number; depth?: number } | null;
  /** Cart-items block authoring — pulled from the slot mapping, same way
   *  CartModal already reads them. Drive description1/2 column content. */
  description1Key?: string;
  description2Key?: string;
  projectLabel?: string;
  headlineTitle?: string;
  /** Active series picked in the Designer — feeds the pd-series block. */
  seriesLabel?: string;
  seriesDescription?: string;
  /** Decimal places for the TOTAL MEASUREMENT value (cm). Defaults to 1 to match
   *  the cart's measurement stat. 0 = whole cm; higher = finer. */
  measurementDecimals?: number;
  /** Decimal separator ("." or ",") for the TOTAL MEASUREMENT value — the same
   *  server-resolved separator the on-screen measurement uses, so the PDF reads
   *  "240,0 × 180,0 cm" when the Designer/language wants a comma. Defaults to ".". */
  decimalSeparator?: string;
  /** Live price breakdown from the host's price source. Fills per-row prices
   *  (pdf-product-price) and the SUBTOTAL / VAT / TOTAL PRICE named totals for
   *  summary-line blocks. Null/absent → the PDF renders without prices. */
  breakdown?: PriceBreakdownDto | null;
}

/** Minimal embed projection of a PdfLayout — just what the renderer + the
 *  screenshot-walker need. Same shape as the (now retired) DesignerConfigResponse
 *  field; lives here so each action call can resolve it on demand instead of
 *  the embed payload pre-shipping a single one. */
export interface PdfLayoutEmbedDto {
  id: string;
  name: string;
  slotMapping: Record<string, unknown> | null;
}

/** Fetch the public projection by id. The embed endpoint is anonymous so the
 *  cart's embed surface can read it without auth. */
export async function fetchPdfLayoutEmbed(
  opts: { baseUrl?: string; pdfLayoutId: string; signal?: AbortSignal }
): Promise<PdfLayoutEmbedDto | null> {
  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const url = `${base}/api/embed/pdf-layout/${encodeURIComponent(opts.pdfLayoutId)}`;
  const res = await fetch(url, { signal: opts.signal });
  if (!res.ok) return null;
  return res.json();
}

/** Group placed instances by configuration the same way CartModal does so the
 *  PDF rows match what the operator sees in the cart. (productId + materialPicks
 *  + variantKey → one row, qty N.) */
export interface GroupedRow {
  groupKey: string;
  product: SceneProductDto | null;
  productId: string;
  materialPicks: Record<string, string>;
  qty: number;
  /** Designer-assigned group membership, when the instances carry it. Two
   *  instances of the same config in different Designer groups stay separate
   *  rows so each group's quantity is correct. Undefined = ungrouped. */
  groupId?: string;
  groupName?: string;
}

export function groupInstancesToRows(
  instances: SceneInstance[],
  productById: Map<string, SceneProductDto>,
): GroupedRow[] {
  const map = new Map<string, GroupedRow>();
  for (const i of instances) {
    const sortedKeys = Object.keys(i.materialPicks).sort();
    const picks = sortedKeys.map(k => `${k}=${i.materialPicks[k]}`).join("&");
    const variantPart = i.variantKey && i.variantKey !== "main" ? `|v=${i.variantKey}` : "";
    // Group membership folds into the aggregation key so the same product in
    // two Designer groups doesn't collapse into one mis-counted row.
    const groupPart = i.groupId ? `|g=${i.groupId}` : "";
    const groupKey = `${i.productId}|${picks}${variantPart}${groupPart}`;
    const existing = map.get(groupKey);
    if (existing) {
      existing.qty += 1;
    } else {
      map.set(groupKey, {
        groupKey,
        product: productById.get(i.productId) ?? null,
        productId: i.productId,
        materialPicks: i.materialPicks,
        qty: 1,
        groupId: i.groupId,
        groupName: i.groupName,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => (a.product?.name ?? "").localeCompare(b.product?.name ?? ""));
}

/** Build the PdfSceneSummaryContext the renderer expects, from current cart
 *  state. Resolves ProductIDs server-side because the SKU template + manual
 *  override resolution lives there. */
export async function buildSceneSummaryContext(
  inputs: PdfRenderContextInputs,
): Promise<PdfSceneSummaryContext> {
  const rows = groupInstancesToRows(inputs.instances, inputs.productById);

  // Same SKU-resolution call CartModal makes — keeps the PDF column matching
  // the cart's article-number column verbatim, including manual overrides.
  let productIds = new Map<string, string>();
  if (rows.length > 0) {
    try {
      const resp = await fetchProductIds({
        baseUrl: inputs.baseUrl,
        body: {
          items: rows.map(r => ({
            groupKey: r.groupKey,
            productId: r.productId,
            materialPicks: r.materialPicks,
          })),
        },
      });
      for (const r of resp.results) {
        if (r.productId) productIds.set(r.groupKey, r.productId);
      }
    } catch {
      // Soft fail — rows just don't get a SKU, mirroring CartModal's tolerance.
    }
  }

  // Breakdown lines keyed by resolved ProductID — the same key the price
  // request was built from, so row ↔ line matching is exact.
  const priceLines = new Map<string, BreakdownLineDto>();
  if (inputs.breakdown) {
    for (const l of inputs.breakdown.lines) priceLines.set(l.productId, l);
  }

  const pdfRows: PdfSummaryRowDto[] = rows.map(r => {
    const params = (r.product?.parameterValues as Record<string, string> | undefined) ?? undefined;
    const priceLine = priceLines.get(productIds.get(r.groupKey) ?? "");
    return {
      productId: productIds.get(r.groupKey) ?? "",
      name: r.product?.name ?? "",
      description1: inputs.description1Key ? params?.[inputs.description1Key] : undefined,
      description2: inputs.description2Key ? params?.[inputs.description2Key] : undefined,
      quantity: r.qty,
      imageUrl: r.product?.imageUrl ?? undefined,
      // Ship every parameter so a pdf-product-parameter block can render any of
      // them, and so the table can sort/group on param:<key>. Category/series
      // are surfaced from conventional parameter keys when the catalog encodes
      // them that way; otherwise operators group by param:<key> directly.
      parameters: params,
      category: params?.["category"] ?? params?.["Category"],
      series: inputs.seriesLabel?.trim() || params?.["series"] || params?.["Series"],
      // Designer-assigned grouping flows through here once instances carry it
      // (Phase 4). Until then rows are ungrouped and "designer-group" grouping
      // collapses to a single implicit group.
      groupId: r.groupId,
      groupName: r.groupName,
      // Resolve the row's finishes to display names here in the browser — the
      // server renderer has no product-detail cache. Empty when the detail
      // isn't cached (caller didn't warm it) or nothing was picked.
      materials: inputs.detailCache
        ? resolveRowMaterials(r.productId, r.materialPicks, inputs.detailCache)
        : [],
      unitPrice: priceLine?.priced
        ? priceLine.formattedUnitAmount ?? `${priceLine.unitAmount} ${inputs.breakdown!.currency}`
        : undefined,
      lineTotal: priceLine?.priced
        ? priceLine.formattedLineTotal ?? `${priceLine.lineTotal} ${inputs.breakdown!.currency}`
        : undefined,
      includedQuantity: priceLine?.includedQuantity || undefined,
    };
  });

  const totals: { label: string; value: string }[] = [];
  if (inputs.dims) {
    // Dimensions are metres → cm. Keep decimals (Math.round dropped them, so a
    // 160.5 cm shelf printed as 161); default to 1 decimal to match the cart's
    // measurement stat (StatBlock kind=measurement uses toFixed(1)). Clamp to a
    // sane 0..3 so a stray config can't explode the string.
    const decimals = Math.min(3, Math.max(0, Math.round(inputs.measurementDecimals ?? 1)));
    const sep = inputs.decimalSeparator === "," ? "," : ".";
    const w = formatDecimal(inputs.dims.width * 100, decimals, sep);
    const h = formatDecimal(inputs.dims.height * 100, decimals, sep);
    totals.push({ label: "TOTAL MEASUREMENT", value: `${w} × ${h} cm` });
  }

  // Price totals — named so summary-line blocks bind to them by label, the
  // same channel TOTAL MEASUREMENT already uses. Adjustments (surcharges,
  // rounding) each get their own labelled line between subtotal and VAT.
  if (inputs.breakdown) {
    const b = inputs.breakdown;
    const fallback = (amount: number) => `${amount} ${b.currency}`;
    totals.push({ label: "SUBTOTAL", value: b.formattedSubtotal ?? fallback(b.subtotal) });
    for (const adj of b.adjustments) {
      totals.push({ label: adj.label.toUpperCase(), value: adj.formattedAmount ?? fallback(adj.amount) });
    }
    if (b.vatPercent != null) {
      totals.push({ label: "VAT", value: b.formattedVatAmount ?? fallback(b.vatAmount) });
    }
    totals.push({ label: "TOTAL PRICE", value: b.formattedTotal ?? fallback(b.total) });
  }

  return {
    projectLabel: inputs.projectLabel?.trim() || "PROJECT INVENTORY",
    headlineTitle: inputs.headlineTitle?.trim() || "Element list",
    seriesLabel: inputs.seriesLabel?.trim() || undefined,
    seriesDescription: inputs.seriesDescription?.trim() || undefined,
    rows: pdfRows,
    totalUnits: rows.reduce((sum, r) => sum + r.qty, 0),
    totals,
    meta: [],
    renderedAtUtc: new Date().toISOString(),
  };
}

/** Walk the PdfLayout's slot mapping and ask Unity for one screenshot per
 *  pdf-unity-screenshot block. Tolerates "no viewer", "no callUnity" — the PDF
 *  just renders without those images. */
export async function captureScreenshotsForPdf(
  pdf: PdfLayoutEmbedDto,
  callUnity:
    | ((method: string, payload?: unknown, timeoutMs?: number) => Promise<string>)
    | undefined,
): Promise<PdfRenderRequestBody["images"]> {
  if (!pdf.slotMapping) return [];
  const requests = collectUnityScreenshotRequests(pdf.slotMapping);
  if (requests.length === 0) return [];
  if (!callUnity) {
    console.warn("[pdfRender] PdfLayout expects Designer screenshots but no viewer handle — skipping captures.");
    return [];
  }
  const captured = await Promise.all(requests.map(req => captureUnityScreenshot(callUnity, req)));
  return captured.filter((c): c is { imageKey: string; base64: string; contentType?: string } => c !== null);
}

/** Render + download. One-shot — caller hands us the inputs + pdfLayoutId,
 *  we resolve the embed projection, capture screenshots, POST, and trigger the
 *  browser download. Throws on render failure. */
export async function downloadPdf(opts: {
  baseUrl?: string;
  pdfLayoutId: string;
  inputs: PdfRenderContextInputs;
  /** Short language code (e.g. "en", "dk") for the layout's own DVar text. */
  lang?: string;
  callUnity?: (method: string, payload?: unknown, timeoutMs?: number) => Promise<string>;
}): Promise<void> {
  (window as any).DConfigAnalytics?.track?.("pdf.requested", { pdfLayoutId: opts.pdfLayoutId, productCount: opts.inputs.instances.length });
  const pdf = await fetchPdfLayoutEmbed({ baseUrl: opts.baseUrl, pdfLayoutId: opts.pdfLayoutId });
  if (!pdf) throw new Error("PDF layout not found");

  const [context, images] = await Promise.all([
    buildSceneSummaryContext(opts.inputs),
    captureScreenshotsForPdf(pdf, opts.callUnity),
  ]);
  const blob = await renderPdf({
    baseUrl: opts.baseUrl,
    pdfLayoutId: pdf.id,
    body: { sceneSummary: context, images, lang: opts.lang },
  });
  const safeName = (pdf.name || "pdf").replace(/[^a-zA-Z0-9-_]+/g, "-");
  downloadBlob(blob, `${safeName}.pdf`);
}

/** Render + email. Same as downloadPdf but ships an email-fields object and
 *  hits the /email endpoint instead. */
export async function sendPdfEmail(opts: {
  baseUrl?: string;
  pdfLayoutId: string;
  email: PdfEmailFields;
  inputs: PdfRenderContextInputs;
  /** Short language code (e.g. "en", "dk") for the layout's own DVar text. */
  lang?: string;
  callUnity?: (method: string, payload?: unknown, timeoutMs?: number) => Promise<string>;
}): Promise<void> {
  (window as any).DConfigAnalytics?.track?.("email.requested", { pdfLayoutId: opts.pdfLayoutId, productCount: opts.inputs.instances.length });
  const pdf = await fetchPdfLayoutEmbed({ baseUrl: opts.baseUrl, pdfLayoutId: opts.pdfLayoutId });
  if (!pdf) throw new Error("PDF layout not found");

  const [context, images] = await Promise.all([
    buildSceneSummaryContext(opts.inputs),
    captureScreenshotsForPdf(pdf, opts.callUnity),
  ]);
  await emailPdf({
    baseUrl: opts.baseUrl,
    pdfLayoutId: pdf.id,
    body: {
      sceneSummary: context,
      images,
      lang: opts.lang,
      email: {
        ...opts.email,
        subject: opts.email.subject?.trim() || pdf.name,
      },
    },
  });
}

/** Send via an EmailLayout (the email is the anchor). The body + subject are
 *  rendered server-side from the layout's translatable blocks; a PDF is attached
 *  only when pdfLayoutId is given, in which case we build the same PDF payload as
 *  downloadPdf/sendPdfEmail and forward it for server-side render + attach. */
export async function sendViaEmailLayout(opts: {
  baseUrl?: string;
  emailLayoutId: string;
  /** Optional PDF attachment layout. */
  pdfLayoutId?: string;
  inputs: PdfRenderContextInputs;
  toEmails: string[];
  ccEmails?: string[];
  bccEmails?: string[];
  projectName?: string;
  lang?: string;
  callUnity?: (method: string, payload?: unknown, timeoutMs?: number) => Promise<string>;
}): Promise<void> {
  (window as any).DConfigAnalytics?.track?.("email.requested", { emailLayoutId: opts.emailLayoutId, pdfLayoutId: opts.pdfLayoutId ?? null, productCount: opts.inputs.instances.length });
  const body: EmailLayoutSendBody = {
    toEmails: opts.toEmails,
    ccEmails: opts.ccEmails,
    bccEmails: opts.bccEmails,
    projectName: opts.projectName,
    lang: opts.lang,
  };

  // Attach a PDF only when one is configured on the action.
  if (opts.pdfLayoutId) {
    const pdf = await fetchPdfLayoutEmbed({ baseUrl: opts.baseUrl, pdfLayoutId: opts.pdfLayoutId });
    if (!pdf) throw new Error("PDF layout not found");
    const [context, images] = await Promise.all([
      buildSceneSummaryContext(opts.inputs),
      captureScreenshotsForPdf(pdf, opts.callUnity),
    ]);
    body.pdfLayoutId = pdf.id;
    body.pdfRenderPayload = { sceneSummary: context, images, lang: opts.lang };
    body.attachmentName = pdf.name;
  }

  await sendEmailLayout({ baseUrl: opts.baseUrl, emailLayoutId: opts.emailLayoutId, body });
}
